import { useUser } from 'components/providers/UserProvider'
import { useEffect } from 'react'
import { InventoryArticle } from 'shared'
import { useLocalForageState } from './useLocalForageState'
import { FetchedStockInterface, useStockStore } from './useStockStore'

/**
 *
 * In the app lifecycle:
 * When I fetchStock() → save the result to localStorage (setFetchedStock).
 * When I fetchDiff() (i.e., polling complete) → save to localStorage (setFetchedStock).
 * When the user modifies items in FE and has not yet saved → I only update the local state of the presenter, not the persistence.
 *
 */

export const usePersistentStock = (
  defaultValue: FetchedStockInterface
): readonly [
  FetchedStockInterface,
  (
    newValue: FetchedStockInterface | ((arg: FetchedStockInterface) => FetchedStockInterface)
  ) => void,
  boolean
] => {
  const { user, activeGame } = useUser()

  const [value, setValue, loading] = useLocalForageState<FetchedStockInterface>(
    defaultValue,
    `${user?.username}-${activeGame?.idGame}-stock`,
    {
      hydrate: (value) => ({
        ...value,
        stock: value.stock.map((a) => new InventoryArticle(a)),
        sealedStock: value.sealedStock.map((a) => new InventoryArticle(a)),
      }),
    }
  )

  const { setFetchedStock } = useStockStore()

  useEffect(() => {
    if (loading) return

    const hasPersistentStock = value && (value.stock?.length > 0 || value.sealedStock?.length > 0)

    // hydrate zustand ONLY if there is something real in the persisted
    if (hasPersistentStock) {
      setFetchedStock(value)
    }
  }, [loading, value, setFetchedStock])

  return [value, setValue, loading]
}
